/* ============================================================================
   Cart drawer — the two things the drawer can't do in HTML alone.

     1. adding a recommendation without leaving the drawer
     2. removing both halves of a bundle as one product

   Everything else the drawer gained — the ladder, the saving, the
   recommendations themselves — is Liquid, deliberately. Dawn re-renders
   `.drawer__inner` from the `cart-drawer` section on every cart change, so
   server-rendered markup already follows the cart for free. Recomputing any of
   it here would be a second definition of the same numbers, and the PDP has
   been bitten by exactly that before.

   Both handlers are delegated from `document`, because the drawer's innards are
   replaced wholesale on every update and anything bound to an element inside it
   would be thrown away on the first click.

   Progressive enhancement throughout: the recommendation is a real form posting
   to /cart/add, so if Dawn's globals are missing the browser's own submit still
   works. Only the bundle remove genuinely needs JS, and it is the one control
   that has no no-JS equivalent — which is fine, because a shopper without JS
   still has the per-line remove buttons the CSS is hiding.
   ========================================================================== */

(function () {
  // Our assets get pulled in by more than one section elsewhere in this theme,
  // and a browser runs every <script src> it is handed — identical URLs are not
  // deduplicated. One click would otherwise send two adds.
  if (window.mgCartInit) return;
  window.mgCartInit = true;

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  function canFetch() {
    return typeof window.routes === 'object' && typeof window.fetchConfig === 'function';
  }

  /**
   * Swaps in freshly rendered sections and puts the drawer back into the right
   * empty/non-empty state.
   *
   * `is-empty` is not cosmetic: Dawn's own CSS keys the whole empty panel off
   * it, and the checkout button off `cart-drawer-items.is-empty`. Removing the
   * last bundle would otherwise leave an empty drawer still showing a totals
   * row and an enabled checkout button.
   */
  function render(state) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !state.sections) return;

    const parser = new DOMParser();

    const drawerHTML = state.sections['cart-drawer'];
    if (drawerHTML) {
      const parsed = parser.parseFromString(drawerHTML, 'text/html');
      const source = parsed.querySelector('.drawer__inner');
      const target = drawer.querySelector('.drawer__inner');
      if (source && target) target.innerHTML = source.innerHTML;
    }

    // `#cart-icon-bubble` is the mount point, not a section wrapper — it is the
    // header's cart <a>, and its own `.shopify-section` ancestor is the entire
    // header. Walking up to that and replacing its innerHTML wiped the logo and
    // the whole nav on every add. Dawn writes into the element itself; so do we.
    const bubbleHTML = state.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && bubble) {
      const parsed = parser.parseFromString(bubbleHTML, 'text/html');
      const source = parsed.querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    const isEmpty = state.item_count === 0;
    drawer.classList.toggle('is-empty', isEmpty);
    drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
    drawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);

    if (!drawer.classList.contains('active')) drawer.open?.();
  }

  function post(url, body) {
    return fetch(url, {
      ...window.fetchConfig(),
      body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: window.location.pathname }),
    }).then((response) => response.json());
  }

  /* ── Adding a recommendation ─────────────────────────────────────────────── */

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-mg-cart-add]');
    if (!form || !canFetch()) return;

    event.preventDefault();

    const button = form.querySelector('button');

    // aria-busy rather than `disabled`: a disabled button loses focus, which
    // drops a keyboard user out of the drawer's focus trap mid-action.
    button?.setAttribute('aria-busy', 'true');

    // Serialised from the form rather than read out of a single `id` field: a
    // component bundle posts `items[N][…]` with line-item properties, and
    // reading one id would have added the bundle line instead of its contents —
    // which is precisely what costs the shopper the discount the row promised.
    // FormData carries whatever shape the Liquid rendered, so the two can't
    // disagree. Content-Type is left unset on purpose; the browser has to write
    // the multipart boundary itself.
    const body = new FormData(form);
    body.append('sections', SECTIONS.join(','));
    body.append('sections_url', window.location.pathname);

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) {
          // A rejected add (sold out between render and click) leaves the
          // drawer as it was rather than half-updating it.
          button?.removeAttribute('aria-busy');
          return;
        }
        // /cart/add.js returns the added line, not the cart, so item_count is
        // absent — fetch the cart to decide the empty state honestly.
        return fetch(`${window.routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => render({ sections: state.sections, item_count: cart.item_count }));
      })
      .catch(() => {
        button?.removeAttribute('aria-busy');
      });
  });

  /* ── Bundle quantity ─────────────────────────────────────────────────────── */

  /**
   * Sets every line of a bundle to the same quantity in one request.
   *
   * One /cart/update.js, not a change.js per line, for the same reason the
   * remove button uses one: two sequential requests would render an
   * intermediate cart holding two Shampoo and one Condition, which is not a
   * Care Duo and would be priced as though it were.
   */
  function setBundleQuantity(group, quantity) {
    const keys = (group.getAttribute('data-mg-bundle-qty') || '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);

    if (!keys.length) return;

    const next = Math.max(0, quantity);
    const updates = {};
    keys.forEach((key) => {
      updates[key] = next;
    });

    group.setAttribute('aria-busy', 'true');

    post(window.routes.cart_update_url, { updates })
      .then((state) => render(state))
      .catch(() => {
        group.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-mg-bundle-step]');
    if (!step || !canFetch()) return;

    event.preventDefault();

    const group = step.closest('[data-mg-bundle-qty]');
    const input = group?.querySelector('[data-mg-bundle-input]');
    if (!group || !input) return;

    // Stepping to 0 removes the bundle, which is what Dawn's own quantity
    // control does on every other line — `min="0"`, and zero drops the item.
    setBundleQuantity(group, Number(input.value) + Number(step.getAttribute('data-mg-bundle-step')));
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-mg-bundle-input]');
    if (!input || !canFetch()) return;

    const group = input.closest('[data-mg-bundle-qty]');
    if (!group) return;

    const typed = Number(input.value);
    // A cleared or nonsensical field is not an instruction to empty the cart.
    if (!Number.isFinite(typed) || input.value.trim() === '') return;

    setBundleQuantity(group, typed);
  });

  /* ── Removing a bundle ───────────────────────────────────────────────────── */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mg-bundle-remove]');
    if (!button || !canFetch()) return;

    event.preventDefault();

    const keys = button
      .getAttribute('data-mg-bundle-remove')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);

    if (!keys.length) return;

    button.setAttribute('aria-busy', 'true');

    // One /cart/update.js with every key at zero, not a change.js per line: two
    // sequential requests would render an intermediate cart holding half a
    // bundle, which is the exact state this control exists to prevent.
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    post(window.routes.cart_update_url, { updates })
      .then((state) => render(state))
      .catch(() => {
        button.removeAttribute('aria-busy');
      });
  });
})();

/* ---------------------------------------------------------------------------
 * Checkout button total vs shipping protection
 *
 * The button prints the cart total. The shipping protection app (Insureful)
 * does not put the protection in the cart: the toggle records intent and the
 * charge is added at checkout, so `cart.total_price` never sees it and the
 * button said £17 while the shopper was about to be charged £19.49.
 *
 * "Auto add to cart", the paid setting, does NOT change that. It only makes the
 * toggle default to on — the line still appears at checkout, not in the cart.
 * An earlier version of this file keyed off `add_to_cart_status` on the
 * assumption that true meant a real cart line; it stood down the moment the
 * setting was switched on and the button went back to under-reporting.
 *
 * So the decision is made from the cart, not from a setting. The Liquid marks
 * the total with `data-mg-protection-in-cart` — server truth about whether an
 * item of type `pt-insurance` is actually in there. If it is, the total already
 * includes it and there is nothing to add. If it is not and the toggle is on,
 * the fee is added for display. That is correct in both of the app's modes and
 * stays correct if it ever gains a third.
 *
 * The price is read from the app's own `ptiSettings` rather than hardcoded, so
 * changing it in the dashboard does not leave a stale number here. A percentage
 * plan is deliberately NOT computed: the app applies cart rules and tiers we
 * cannot see, and a number we half-guess is worse than the plain cart total.
 * In that case the button is left alone.
 */
(() => {
  if (window.mgCartProtectionBound) return;
  window.mgCartProtectionBound = true;

  const TOGGLE = '#pti-insurence-needed';
  const TOTAL = '.mg-cta__total';

  const formatMoney = (pence) => {
    const pounds = pence / 100;
    const body = Number.isInteger(pounds) ? String(pounds) : pounds.toFixed(2);
    return `£${body}`;
  };

  // Only a fixed plan is safe to add to a number we display. `insurance_plan_type`
  // 1 is the app's fixed plan; anything else is percentage or rule-driven.
  const protectionPence = () => {
    const s = window.ptiSettings;
    if (!s || s.app_status === false) return 0;
    if (Number(s.insurance_plan_type) !== 1) return 0;
    const price = Number.parseFloat(s.fixed_plan_price);
    if (!Number.isFinite(price) || price <= 0) return 0;
    return Math.round(price * 100);
  };

  const sync = () => {
    document.querySelectorAll(TOTAL).forEach((el) => {
      const base = Number.parseInt(el.getAttribute('data-mg-cart-total'), 10);
      if (!Number.isFinite(base)) return;
      // Already a line in the cart: `base` covers it, adding again would double.
      if (el.getAttribute('data-mg-protection-in-cart') === 'true') {
        el.textContent = formatMoney(base);
        return;
      }
      const on = document.querySelector(TOGGLE)?.checked === true;
      el.textContent = formatMoney(on ? base + protectionPence() : base);
    });
  };

  // The toggle is injected by the app after this script runs, and is replaced
  // outright whenever Dawn re-renders the drawer — so the listener is delegated
  // from the document rather than bound to the element.
  document.addEventListener('change', (event) => {
    if (event.target.closest?.(TOGGLE)) sync();
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('.pti-switch')) requestAnimationFrame(sync);
  });

  // A cart change re-renders the button from the server, which resets it to the
  // plain cart total while the toggle may still be on. Re-apply after each one.
  //
  // NOT via Dawn's pub/sub: `subscribe` and `PUB_SUB_EVENTS` are module-scoped
  // in Dawn 16, not globals, so the guard below was always false and the
  // subscription never bound. That is why the button stayed on the plain cart
  // total after an add-to-cart auto-opened the drawer — there was no click on
  // the cart icon to re-trigger the poll, and no event either.
  //
  // A MutationObserver on the drawer catches every path instead: Dawn's own
  // re-render, the recommendation add, the bundle remove, and the app
  // re-injecting its widget. Each burst is followed by a short settle, because
  // the app sets `.checked` a moment after its markup lands.
  document.addEventListener('cart:refresh', sync);

  const watchDrawer = () => {
    const target = document.querySelector('#CartDrawer');
    if (!target) return;
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        sync();
        settle();
      }, 150);
    }).observe(target, { childList: true, subtree: true });
  };

  // The app writes its widget in late AND sets the checkbox's `checked`
  // property directly, which fires no event of any kind — so there is nothing
  // to listen for. It was leaving the button on the plain cart total while the
  // toggle already showed protection as on, the two visibly disagreeing.
  //
  // A MutationObserver does not help either: assigning `.checked` changes the
  // property, not the attribute, so no mutation is recorded. A short poll is
  // the honest tool here. Bounded to the first few seconds after load, then it
  // stops — the event listeners above cover every change after that.
  const settle = () => {
    let ticks = 0;
    const id = setInterval(() => {
      sync();
      if (++ticks >= 12) clearInterval(id);
    }, 400);
  };

  document.addEventListener('DOMContentLoaded', () => {
    sync();
    settle();
    watchDrawer();
  });
  // Re-settle when the drawer is opened: the widget is often mounted or
  // re-mounted at that moment rather than at page load.
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('#cart-icon-bubble')) settle();
  });
  sync();
  settle();
  watchDrawer();
})();

/* ---------------------------------------------------------------------------
 * Open the drawer after a /cart bounce
 *
 * `layout/theme.liquid` redirects /cart back to wherever the shopper came from
 * and leaves a flag. This picks it up and opens the drawer there, so following
 * a cart link still ends with the cart in front of them rather than silently
 * dropping them on a page they did not ask for.
 *
 * It waits on `customElements.whenDefined` rather than DOMContentLoaded. This
 * file is deferred and so is Dawn's `cart.js`, so by the time this runs the
 * <cart-drawer> element is in the DOM but has often not upgraded yet — it is
 * still an unknown element with no `open` method on it. The first version
 * checked for that method, found nothing, and returned; because it had already
 * cleared the flag by then, the bounce silently did nothing.
 *
 * The flag is cleared only once the drawer has actually been asked to open, so
 * a failure leaves it to be retried rather than swallowing it. The timeout is
 * the backstop: if the element never upgrades the flag still goes, or it would
 * reopen the drawer on every page for the rest of the session.
 * ------------------------------------------------------------------------ */
(() => {
  if (window.mgCartBounceBound) return;
  window.mgCartBounceBound = true;

  let flagged = false;
  try {
    flagged = sessionStorage.getItem('mg-open-cart') === '1';
  } catch (e) {
    return;
  }
  if (!flagged) return;

  const clear = () => {
    try {
      sessionStorage.removeItem('mg-open-cart');
    } catch (e) {
      /* nothing useful to do */
    }
  };

  const timer = setTimeout(clear, 8000);

  // Open by clicking the header cart icon rather than calling `drawer.open()`
  // directly. Dawn binds that click itself in the CartDrawer constructor, so
  // going through it means the drawer opens exactly as it does for a shopper —
  // same focus handling, same guards. Calling `open()` straight after the
  // element upgrades did nothing: the call ran, but the `animate active`
  // classes never landed, and the drawer sat closed while the flag was gone.
  //
  // `window.load` rather than DOMContentLoaded, because the click listener is
  // attached by Dawn's own deferred script and only the load event guarantees
  // every deferred script has run.
  const openDrawer = () => {
    customElements.whenDefined('cart-drawer').then(() => {
      const icon = document.querySelector('#cart-icon-bubble');
      const drawer = document.querySelector('cart-drawer');
      if (!icon || !drawer) return;

      icon.click();

      // If the click did not take — the listener is Dawn's and we do not own
      // its timing — fall back to the element's own method before giving up.
      setTimeout(() => {
        if (!drawer.classList.contains('active') && typeof drawer.open === 'function') {
          drawer.open(icon);
        }
        clearTimeout(timer);
        clear();
      }, 300);
    });
  };

  if (document.readyState === 'complete') {
    openDrawer();
  } else {
    window.addEventListener('load', openDrawer, { once: true });
  }
})();

/* ---------------------------------------------------------------------------
 * Announce the delivery ladder
 *
 * The ladder used to carry `role="status"` and never announced a thing. Dawn
 * replaces `.drawer__inner`'s innerHTML on every cart change, so the ladder is
 * destroyed and rebuilt rather than updated — and a live region only announces
 * changes made to a region that already existed. A sighted shopper watches the
 * bar move; a screen reader user got silence, from an attribute that made the
 * problem look solved.
 *
 * So the announcer lives outside the re-rendered subtree (see the note in
 * cart-drawer.liquid) and this copies the ladder's message into it.
 *
 * A MutationObserver rather than the cartUpdate event, because the ladder is
 * rewritten by several paths — Dawn's own cart updates, the recommendation
 * add, the bundle remove — and the observer catches all of them without each
 * one having to remember to call this.
 * ------------------------------------------------------------------------ */
(() => {
  if (window.mgLadderAnnouncerBound) return;
  window.mgLadderAnnouncerBound = true;

  const region = () => document.querySelector('[data-mg-cart-announcer]');
  const message = () =>
    document.querySelector('.mg-ladder__message')?.textContent.replace(/\s+/g, ' ').trim() ?? '';

  // Seeded with what is on screen at load, so the first real change is the
  // first thing announced.
  let last = message();

  const announce = () => {
    const now = message();
    if (now === last) return;
    last = now;
    const el = region();
    if (!el) return;
    // Cleared first, then written on a later tick: re-writing identical text
    // into a live region is not a change, and some screen readers stay silent
    // if the clear and the write land in the same task.
    //
    // setTimeout, NOT requestAnimationFrame. A hidden document never fires rAF
    // at all — the same trap the accordion and the reviews carousel hit — so a
    // cart updated in a background tab would clear the region, never write to
    // it, and leave `last` already advanced so the message was skipped for
    // good. Timers still run when hidden, throttled but reliable.
    el.textContent = '';
    setTimeout(() => {
      el.textContent = now;
    }, 60);
  };

  const start = () => {
    const target = document.querySelector('#CartDrawer');
    if (!target) return;
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      // Coalesce: one cart update fires many mutations as the subtree is
      // rebuilt, and announcing per mutation would stutter.
      setTimeout(() => {
        queued = false;
        announce();
      }, 120);
    }).observe(target, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
